/* ------------------------------------------------------------------------
  Copyright (c) 2016, VoiceVibes, Inc

  VoiceVibes, Inc - PROPRIETARY/CONFIDENTIAL
  All contents of this file are proprietary and confidential. No part of
  any of the contents of this file may be disclosed in any manner to
  a third party without the prior written consent of VoiceVibes, Inc.
  Unauthorized copying of this file, via any medium is strictly prohibited.
----------------------------------------------------------------------------- */


<template>
  <div class="speakerDash">
    <div
      v-if="loading"
      class="text-center"
    >
      <h2>
        <svg
          class="icon-refresh rotate large-spinner"
        >
          <use xlink:href="/static/svg-symbols.svg#icon-refresh" />
        </svg>
        Loading...
      </h2>
    </div>

    <div v-if="!loading">
      <!--  Second row of status bar: assignments/members toggle, stats  -->
      <div class="row">
        <div
          id="assignments-members-toggler"
          class="col-sm-4"
        >
          <span
            :class="speakerDashboardToggle === 'assignments' ? 'active' : 'hoverLink'"
            @click.prevent="toggle('assignments')"
          >
            Assignments
          </span>
          <span id="assignments-members-toggler-divider">
            |
          </span>
          <span
            :class="speakerDashboardToggle === 'library' ? 'active' : 'hoverLink'"
            @click.prevent="toggle('library')"
          >
            My Library
          </span>
        </div>
        <div
          id="stats"
          class="hidden-xs col-sm-8"
        >
          <div
            v-for="stat in stats"
            :key="stat.name"
            class="stat"
            :class="[ 'col-xs-' + Math.floor(10 / stats.length) ]"
          >
            <div class="stat-value">
              {{ stat.value }}
            </div>
            <div class="stat-name">
              {{ stat.name }}
            </div>
          </div>

          <!--  Wordspotting Button  -->
          <router-link
            v-if="showWordspotting"
            :to="{ name: 'wordspotting' }"
            style="margin-left: 10px;"
          >
            <button
              class="btn btn-primary clear-btn"
            >
              Wordspotting
            </button>
          </router-link>
        </div>
      </div>

      <speaker-assignments
        v-if="speakerDashboardToggle === 'assignments'"
        :assignments="augmentedAssignments"
        :groups="speakerGroups"
        :me="me"
      />

      <my-library
        v-if="speakerDashboardToggle === 'library'"
        :assignments="augmentedAssignments"
        :groups="speakerGroups"
        :me="me"
        :recordings="augmentedRecordings"
      />
    </div>
  </div>
</template>

<style lang="less" scoped>
  @import '../../less/bootstrap-vars.less';
  @import '~bootstrap/less/mixins/buttons.less';

  #assignments-members-toggler {
    font-size: 18px;
    line-height: 24.51px;
    color: @link-color;
    cursor: pointer;
  }
  #assignments-members-toggler-divider {
    color: #333;
  }
  #assignments-members-toggler span.active {
    color: #333;
    cursor: default;
  }
  #stats .stat {
    margin-right: 15px;
    display: flex;
    color: #3E3E3E;
  }
  #stats .stat-value {
    white-space:nowrap;
    font-size: 24px;
    line-height: 32.68px;
    font-weight: 600;
    margin-right: 3px;
  }
  #stats .stat-name {
    font-size: 12px;
    line-height: 16.34px;
    font-weight: 400;
  }
</style>

<script>

import SpeakerAssignments from '../layout/SpeakerAssignments';
import MyLibrary from '../layout/MyLibrary';
import BasePage from '../pages/BasePage';
import AssignmentDataStructures from '../specials/AssignmentDataStructures';
import ScorecardMixin from '../specials/ScorecardMixin';
import AssignmentModes from '../specials/AssignmentModes';

export default {
  components: {SpeakerAssignments, MyLibrary},
  mixins: [BasePage, AssignmentDataStructures, AssignmentModes, ScorecardMixin],
  data() {
    return {
      loading: true,
      speakerDashboardToggle: null,
    };
  },
  computed: {
    assignments: {
      cache: false,
      get() {
        return this.$store.getters.assignedAssignments;
      }
    },
    recordings: {
      cache: false,
      get() {
        return this.$store.getters.recordings;
      }
    },
    showWordspotting() {
      const org = this.$store.getters.org;
      return (org && typeof org.settings !== 'undefined' && org.settings.Custom_Wordspotting_For_Users);
    },
    statActiveAssignments() {
      return _.filter(this.augmentedAssignments, a => {
        // Do not count assignments which are archived or deleted
        if (a.archived || a.deleted || a.completionStatus && (a.completionStatus.status == 'completed')) {
          return false;
        }
        return true;
      }).length;
    },
    statTotalRecordings() {
      return this.recordings ? this.recordings.length : 0;
    },
    statTotalComments() {
      // for each non-deleted, non-archived assignment, iterate over recordings and accumulate number of comments for each.
      return _.filter(this.augmentedAssignments, a => !a.archived && !a.deleted)
        .reduce((previousA, a) => {
          return previousA + a.recordings.reduce(
            (previousR, r) => {
              return (r.userId == this.me.userId && r.feedback) ? r.feedback.length : 0;
            },
            0
          );
        }, 0);
    },
    statRecordingsSubmitted() {
      return this.recordings.filter( r => r.submitted ).length;
    },
    stats() {
      return [
        { name: 'Active Assignments', value: this.statActiveAssignments },
        { name: 'Total Recordings', value: this.statTotalRecordings },
        { name: 'Total Comments', value: this.statTotalComments },
        { name: 'Recordings Flagged', value: this.statRecordingsSubmitted }
      ];
    }
  },
  methods: {
    basePageCreated() {
      this.loadAllAssignmentsData(false).then(() => {
        if (!this.assignmentsEnabled) {
          return this.$router.push({ name: 'manage' });
        }
        this.loadCurrentModeFromPreferences();

        if (this.preferences && this.preferences.speakerDashboardToggle) {
          this.speakerDashboardToggle = this.preferences.speakerDashboardToggle;
        } else {
          this.speakerDashboardToggle = 'assignments';
        }
        this.loading = false;
      });
    },
    toggle(mode) {
      this.speakerDashboardToggle = mode;
      this.$store.dispatch(
        'updatePreferences',
        Object.assign(this.preferences, {speakerDashboardToggle: this.speakerDashboardToggle})
      );
    }
  }
};
</script>
